import React, { useEffect, useRef, useState } from 'react'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CCollapse,
  CRow,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilCheckAlt,
  cilCheckCircle,
  cilChevronBottom,
  cilChevronTop,
  cilCloudUpload,
  cilFile,
  cilInfo,
} from '@coreui/icons'
import ToastComponent from './TaostComponent'

// The chrome every bulk-import wizard shares - the stepper, the tiles, the info boxes and the
// upload step - lifted out of the individual wizards once a second screen needed them. What a
// given import validates and how it commits stays in that wizard; only the parts that were
// identical in every copy live here. See the "CSV / spreadsheet imports" section of the README.

export const MAX_IMPORT_FILE_SIZE = 10 * 1024 * 1024 // 10MB

export const TONE_HEX = {
  success: '#198754',
  danger: '#dc3545',
  warning: '#b45309',
  info: '#087990',
  secondary: '#495057',
}

export const tintStyle = (hex, alphaHex) => ({ backgroundColor: `${hex}${alphaHex}` })

export const MiniStepper = ({ steps, current, maxReached, onStepClick }) => (
  <div className="d-flex align-items-center mb-4 flex-wrap">
    {steps.map((label, idx) => {
      const n = idx + 1
      const done = n < current
      const active = n === current
      const clickable = n <= maxReached
      return (
        <React.Fragment key={label}>
          <div
            className="d-flex align-items-center gap-2"
            style={{ cursor: clickable ? 'pointer' : 'default', opacity: clickable ? 1 : 0.5 }}
            onClick={() => clickable && onStepClick(n)}
          >
            <div
              className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
                done ? 'bg-success text-white' : active ? 'bg-primary text-white' : 'bg-body border'
              }`}
              style={{ width: 28, height: 28, fontSize: 13 }}
            >
              {done ? <CIcon icon={cilCheckAlt} size="sm" /> : n}
            </div>
            <span className={`small ${active ? 'fw-semibold text-primary' : ''}`}>{label}</span>
          </div>
          {n < steps.length && (
            <div
              className={`mx-2 ${done ? 'bg-success' : 'bg-secondary-subtle'}`}
              style={{ height: 2, width: 32 }}
            />
          )}
        </React.Fragment>
      )
    })}
  </div>
)

export const InfoBox = ({ children, color = 'info', className = '' }) => (
  <CAlert color={color} className={`d-flex align-items-start gap-2 border-0 ${className}`}>
    <CIcon icon={cilInfo} className="flex-shrink-0 mt-1" />
    <div>{children}</div>
  </CAlert>
)

export const StatTile = ({ label, value, tone, icon }) => {
  const hex = TONE_HEX[tone] || TONE_HEX.secondary
  return (
    <div
      className="rounded-4 p-3 flex-fill d-flex align-items-center gap-3"
      style={{ ...tintStyle(hex, '14'), border: `1px solid ${hex}33` }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
        style={{ width: 38, height: 38, backgroundColor: hex, color: '#fff' }}
      >
        <CIcon icon={icon} size="sm" />
      </div>
      <div>
        <div className="fs-4 fw-bold lh-1" style={{ color: hex }}>
          {value}
        </div>
        <div className="small text-medium-emphasis">{label}</div>
      </div>
    </div>
  )
}

export const ImportErrorAlert = ({ errors, title = 'The import could not be run:' }) =>
  errors && errors.length ? (
    <CAlert color="danger">
      <div className="fw-semibold mb-2">{title}</div>
      <ul className="mb-0 ps-3">
        {errors.map((error, index) => (
          <li key={index}>{error}</li>
        ))}
      </ul>
    </CAlert>
  ) : null

// The dashed drop zone, and the file card it turns into once something is loaded. Enforces the
// size cap itself so no caller can forget it.
export const FileDropZone = ({ fileName, fileDetail, disabled = false, onFile, onRemove }) => {
  const inputRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)

  const accept = (file) => {
    if (!file) return
    if (file.size > MAX_IMPORT_FILE_SIZE) {
      ToastComponent('File is too large. Maximum size is 10MB.', 'error')
      return
    }
    onFile(file)
  }

  if (fileName) {
    return (
      <CCard className="border-secondary-subtle rounded-3">
        <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <CIcon icon={cilFile} className="text-success" size="xl" />
            <div>
              <div className="fw-semibold">{fileName}</div>
              <div className="small text-success d-flex align-items-center gap-1">
                <CIcon icon={cilCheckCircle} size="sm" />
                {fileDetail}
              </div>
            </div>
          </div>
          <CButton type="button" color="link" className="text-danger p-0" onClick={onRemove}>
            Remove
          </CButton>
        </CCardBody>
      </CCard>
    )
  }

  return (
    <div
      className="border rounded-4 d-flex flex-column align-items-center justify-content-center text-center p-4"
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        minHeight: 180,
        backgroundColor: isDragging ? 'rgba(var(--cui-primary-rgb), 0.06)' : undefined,
        borderStyle: 'dashed',
        opacity: disabled ? 0.6 : 1,
      }}
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setIsDragging(true)
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setIsDragging(false)
        if (!disabled) accept(e.dataTransfer.files?.[0])
      }}
    >
      <CIcon icon={cilCloudUpload} size="xl" className="text-primary mb-2" />
      <div>
        Drag and drop an XLSX or CSV file, or <span className="text-primary">browse</span>
      </div>
      <div className="text-medium-emphasis small mt-1">Maximum file size 10MB</div>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept=".csv,.xlsx,.xls"
        disabled={disabled}
        onChange={(e) => {
          accept(e.target.files?.[0])
          e.target.value = ''
        }}
      />
    </div>
  )
}

// Step 1 of every wizard: what to fill in on the left, where to drop it on the right.
export const ImportUploadStep = ({
  templateTitle = 'Download Template',
  templateHint,
  templateButton,
  requiredColumns,
  info,
  fileName,
  fileDetail,
  busy = false,
  busyLabel = 'Reading and validating your file…',
  disabled = false,
  onFile,
  onRemove,
}) => (
  <CRow className="g-4">
    <CCol lg={5}>
      <CCard className="border-secondary-subtle rounded-4 h-100">
        <CCardBody>
          <div className="fw-semibold fs-6 mb-1">{templateTitle}</div>
          {templateHint && <div className="small mb-3">{templateHint}</div>}
          {templateButton && <div className="mb-4">{templateButton}</div>}
          {requiredColumns && (
            <>
              <div className="fw-semibold mb-2">Required columns</div>
              <ul className="ps-3 mb-3 small">
                {requiredColumns.map((column, index) => (
                  <li key={index}>{column}</li>
                ))}
              </ul>
            </>
          )}
          {info && <InfoBox color="light">{info}</InfoBox>}
        </CCardBody>
      </CCard>
    </CCol>

    <CCol lg={7}>
      <CCard className="border-secondary-subtle rounded-4 h-100">
        <CCardBody>
          <div className="fw-semibold fs-6 mb-1">Upload Completed Spreadsheet</div>
          <div className="small mb-3">
            Drag and drop an XLSX or CSV file, or browse your computer.
          </div>
          <FileDropZone
            fileName={fileName}
            fileDetail={fileDetail}
            disabled={disabled}
            onFile={onFile}
            onRemove={onRemove}
          />
          {busy && (
            <div className="d-flex align-items-center gap-2 small text-medium-emphasis mt-2">
              <CSpinner size="sm" />
              {busyLabel}
            </div>
          )}
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
)

// The file summary bar at the top of the validate step.
export const ImportFileBar = ({ fileName, detail, onReplace }) => (
  <CCard className="border-secondary-subtle rounded-4 mb-3">
    <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div className="d-flex align-items-center gap-2">
        <CIcon icon={cilFile} className="text-primary" />
        <div>
          <div className="fw-semibold">{fileName}</div>
          <div className="small text-medium-emphasis">{detail}</div>
        </div>
      </div>
      <CButton type="button" color="secondary" variant="outline" size="sm" onClick={onReplace}>
        Replace File
      </CButton>
    </CCardBody>
  </CCard>
)

// A wizard folded into a page that has its own job, rather than given a route of its own: the
// panel stays shut, and mounts nothing - so reads no file and fetches nothing - until it is
// opened for the first time.
export const CollapsibleImportCard = ({ title, description, open, onToggle, children }) => {
  const [mounted, setMounted] = useState(open)
  useEffect(() => {
    if (open) setMounted(true)
  }, [open])

  return (
    <CCard className="mb-3">
      <CCardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <strong>{title}</strong>
          {description && <div className="text-medium-emphasis small">{description}</div>}
        </div>
        <CButton
          type="button"
          color="secondary"
          variant="outline"
          size="sm"
          onClick={onToggle}
          aria-expanded={open}
        >
          <CIcon icon={open ? cilChevronTop : cilChevronBottom} className="me-1" />
          {open ? 'Hide' : 'Show'}
        </CButton>
      </CCardHeader>
      <CCollapse visible={open}>
        <CCardBody>{mounted && children}</CCardBody>
      </CCollapse>
    </CCard>
  )
}
